// src/admin/AIFraudSettings.jsx
// AI Fraud Detection Settings Panel - WARNING ONLY SYSTEM
// ⚠️ IMPORTANT: This system ONLY generates warnings. It NEVER automatically suspends or restricts accounts.

import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase-config';
import { 
  Settings, Shield, AlertTriangle, Bell, Activity, 
  Globe, DollarSign, Users, TrendingUp, Clock,
  Info, Save, RotateCcw, AlertCircle, Check
} from 'lucide-react';

const AIFraudSettings = () => {
  const functions = getFunctions();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Default settings structure
  const defaultSettings = {
    enabled: true,
    warningOnly: true, // ALWAYS TRUE - Never auto-suspend
    
    // Account Velocity
    accountVelocity: {
      maxAccountsPerIpDay: 3,
      maxAccountsPerIpWeek: 10,
      maxAccountsPerDeviceDay: 2,
      velocityWindowHours: 24,
      minTimeBetweenAccountsMinutes: 30
    },
    
    // Referral Patterns
    referralPatterns: {
      maxReferralsPerDay: 5,
      maxReferralsPerWeek: 20,
      maxReferralsSameIp: 3,
      minTimeBetweenReferralsMinutes: 60,
      selfReferralDetection: true,
      circularReferralDetection: true,
      conversionRateThreshold: 80
    },
    
    // Payment Anomalies
    paymentAnomalies: {
      maxPaymentAttemptsPerHour: 10,
      maxFailedPayments: 5,
      unusualAmountThreshold: 500,
      multiplePaymentMethodsThreshold: 3,
      rapidPaymentVelocity: 5,
      geographicInconsistency: true
    },
    
    // Request Patterns
    requestPatterns: {
      maxRequestsPerHour: 20,
      maxRequestsSameMusicianDay: 10,
      minTimeBetweenRequestsSeconds: 30,
      duplicateDetectionWindowMinutes: 5,
      requestToPaymentRatioThreshold: 10
    },
    
    // Performance Manipulation
    performanceManipulation: {
      maxRatingsPerDay: 10,
      consistentRatingDetection: true,
      ratingVelocityPerHour: 5,
      sameIpRatingDetection: true,
      minPerformanceDurationSeconds: 30,
      performanceRecyclingDetection: true
    },
    
    // Geographic Anomalies
    geographicAnomalies: {
      multipleLocationThreshold: 3,
      impossibleTravelDetection: true,
      vpnProxyDetection: true,
      vpnProxyAction: 'flag', // flag, ignore, block (but block is disabled in warning mode)
      maxConcurrentSessions: 3,
      sessionHoppingThreshold: 10
    },
    
    // Financial Thresholds
    financialThresholds: {
      dailyEarningLimit: 1000,
      weeklyEarningLimit: 5000,
      voucherRedemptionVelocity: 5,
      unusualVoucherAmount: 500,
      multipleVoucherTypesDay: 3
    },
    
    // Risk Scoring
    riskScoring: {
      accountVelocityWeight: 30,
      referralPatternWeight: 25,
      paymentAnomalyWeight: 35,
      performancePatternWeight: 20,
      geographicAnomalyWeight: 15,
      overallRiskThreshold: 70
    },
    
    // Alert Configuration
    alertConfig: {
      alertThreshold: 70,
      autoSuspendThreshold: 999, // Set impossibly high - never auto-suspend
      alertCooldownHours: 24,
      batchAlertFrequencyMinutes: 60,
      emailNotifications: true,
      smsNotificationsForCritical: false,
      dashboardPriority: 'medium'
    },
    
    // Grace Periods
    gracePeriods: {
      newUserGraceDays: 7,
      verifiedUserRelaxed: true,
      premiumUserExemptions: false,
      whitelistedUsers: []
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Use HTTP version to avoid CORS issues
      const idToken = await auth.currentUser?.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/getAIFraudSettingsHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSettings(result.settings || defaultSettings);
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (category, field, value) => {
    const newSettings = { ...settings };
    if (category) {
      newSettings[category][field] = value;
    } else {
      newSettings[field] = value;
    }
    setSettings(newSettings);
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    setSaveSuccess(false);
    
    try {
      // Use HTTP version to avoid CORS issues
      const idToken = await auth.currentUser?.getIdToken();
      
      const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/updateAIFraudSettingsHttp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ settings })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSaveSuccess(true);
        setHasChanges(false);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error(result.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset all settings to defaults? This cannot be undone.')) {
      setSettings(defaultSettings);
      setHasChanges(true);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'account', label: 'Account Creation', icon: Users },
    { id: 'referral', label: 'Referrals', icon: TrendingUp },
    { id: 'payment', label: 'Payments', icon: DollarSign },
    { id: 'request', label: 'Requests', icon: Activity },
    { id: 'performance', label: 'Ratings', icon: TrendingUp },
    { id: 'geographic', label: 'Geographic', icon: Globe },
    { id: 'financial', label: 'Financial', icon: DollarSign },
    { id: 'risk', label: 'Risk Scoring', icon: Shield },
    { id: 'alerts', label: 'Alerts', icon: Bell }
  ];

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Activity className="animate-spin" size={48} style={{ margin: '0 auto', color: '#7C3AED' }} />
        <p style={{ marginTop: '20px', color: '#6B7280' }}>Loading fraud detection settings...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      {/* Warning Banner - ALWAYS VISIBLE */}
      <div style={{
        backgroundColor: '#FEF3C7',
        border: '3px solid #F59E0B',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '30px',
        display: 'flex',
        alignItems: 'start',
        gap: '16px'
      }}>
        <AlertTriangle size={32} style={{ color: '#D97706', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <h3 style={{ margin: '0 0 8px 0', color: '#92400E', fontSize: '20px', fontWeight: 'bold' }}>
            ⚠️ WARNING-ONLY SYSTEM
          </h3>
          <p style={{ margin: '0 0 8px 0', color: '#78350F', fontSize: '16px', lineHeight: '1.5' }}>
            This AI fraud detection system <strong>ONLY generates warnings and alerts</strong>. 
            It will <strong>NEVER</strong> automatically suspend, restrict, or impact any musician accounts.
          </p>
          <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>
            All account actions must be manually reviewed and executed by an administrator. 
            Musicians can continue using the platform normally regardless of any fraud alerts.
          </p>
        </div>
      </div>

      {/* Header */}
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: 'white', 
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Shield size={32} />
            AI Fraud Detection Settings
          </h2>
          <p style={{ color: '#9CA3AF', margin: 0 }}>
            Configure detection thresholds and sensitivity for the warning system
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          {saveSuccess && (
            <div style={{
              backgroundColor: '#10B981',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Check size={20} />
              Saved!
            </div>
          )}
          
          <button
            onClick={resetToDefaults}
            style={{
              backgroundColor: '#6B7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RotateCcw size={18} />
            Reset to Defaults
          </button>
          
          <button
            onClick={saveSettings}
            disabled={!hasChanges || saving}
            style={{
              backgroundColor: hasChanges ? '#7C3AED' : '#4B5563',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: saving ? 0.7 : 1
            }}
          >
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        backgroundColor: '#374151',
        borderRadius: '12px',
        padding: '8px',
        marginBottom: '24px',
        display: 'flex',
        gap: '8px',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                backgroundColor: activeTab === tab.id ? '#7C3AED' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#9CA3AF',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{
        backgroundColor: '#1F2937',
        borderRadius: '12px',
        padding: '24px'
      }}>
        {/* General Settings */}
        {activeTab === 'general' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              General Settings
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  cursor: 'pointer'
                }}>
                  <div>
                    <p style={{ color: 'white', margin: '0 0 4px 0', fontWeight: 'bold' }}>
                      Fraud Detection System
                    </p>
                    <p style={{ color: '#9CA3AF', margin: 0, fontSize: '14px' }}>
                      Enable or disable the AI fraud detection system
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => updateSetting(null, 'enabled', e.target.checked)}
                    style={{ 
                      width: '24px', 
                      height: '24px', 
                      cursor: 'pointer',
                      accentColor: '#7C3AED'
                    }}
                  />
                </label>
              </div>

              <div style={{
                backgroundColor: '#FEF3C7',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                gap: '12px'
              }}>
                <Info size={20} style={{ color: '#D97706', flexShrink: 0 }} />
                <div>
                  <p style={{ color: '#92400E', margin: '0 0 4px 0', fontWeight: 'bold' }}>
                    Warning-Only Mode (Always On)
                  </p>
                  <p style={{ color: '#78350F', margin: 0, fontSize: '14px' }}>
                    The system operates in warning-only mode and cannot automatically suspend accounts.
                    All actions require manual admin review.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Account Velocity Settings */}
        {activeTab === 'account' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Account Creation Monitoring
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Max Accounts per IP (Daily)"
                value={settings.accountVelocity.maxAccountsPerIpDay}
                onChange={(val) => updateSetting('accountVelocity', 'maxAccountsPerIpDay', val)}
                type="number"
                min={1}
                max={20}
                help="Alert when more than this many accounts are created from same IP in 24 hours"
              />
              
              <SettingInput
                label="Max Accounts per IP (Weekly)"
                value={settings.accountVelocity.maxAccountsPerIpWeek}
                onChange={(val) => updateSetting('accountVelocity', 'maxAccountsPerIpWeek', val)}
                type="number"
                min={1}
                max={50}
                help="Alert when more than this many accounts are created from same IP in 7 days"
              />
              
              <SettingInput
                label="Max Accounts per Device (Daily)"
                value={settings.accountVelocity.maxAccountsPerDeviceDay}
                onChange={(val) => updateSetting('accountVelocity', 'maxAccountsPerDeviceDay', val)}
                type="number"
                min={1}
                max={10}
                help="Alert when multiple accounts created from same device fingerprint"
              />
              
              <SettingInput
                label="Minimum Time Between Accounts (minutes)"
                value={settings.accountVelocity.minTimeBetweenAccountsMinutes}
                onChange={(val) => updateSetting('accountVelocity', 'minTimeBetweenAccountsMinutes', val)}
                type="number"
                min={5}
                max={120}
                help="Alert when accounts created too quickly in succession"
              />
            </div>
          </div>
        )}

        {/* Referral Settings */}
        {activeTab === 'referral' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Referral System Monitoring
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Max Referrals per Day"
                value={settings.referralPatterns.maxReferralsPerDay}
                onChange={(val) => updateSetting('referralPatterns', 'maxReferralsPerDay', val)}
                type="number"
                min={1}
                max={20}
                help="Alert when user creates more referrals than this in 24 hours"
              />
              
              <SettingInput
                label="Max Referrals per Week"
                value={settings.referralPatterns.maxReferralsPerWeek}
                onChange={(val) => updateSetting('referralPatterns', 'maxReferralsPerWeek', val)}
                type="number"
                min={1}
                max={100}
                help="Alert when user creates more referrals than this in 7 days"
              />
              
              <SettingInput
                label="Max Referrals from Same IP"
                value={settings.referralPatterns.maxReferralsSameIp}
                onChange={(val) => updateSetting('referralPatterns', 'maxReferralsSameIp', val)}
                type="number"
                min={1}
                max={10}
                help="Alert when multiple referrals come from the same IP address"
              />
              
              <SettingInput
                label="Conversion Rate Alert (%)"
                value={settings.referralPatterns.conversionRateThreshold}
                onChange={(val) => updateSetting('referralPatterns', 'conversionRateThreshold', val)}
                type="number"
                min={50}
                max={100}
                help="Alert when referral conversion rate exceeds this percentage"
              />
              
              <SettingToggle
                label="Self-Referral Detection"
                value={settings.referralPatterns.selfReferralDetection}
                onChange={(val) => updateSetting('referralPatterns', 'selfReferralDetection', val)}
                help="Alert when detecting potential self-referral patterns"
              />
              
              <SettingToggle
                label="Circular Referral Detection"
                value={settings.referralPatterns.circularReferralDetection}
                onChange={(val) => updateSetting('referralPatterns', 'circularReferralDetection', val)}
                help="Alert when detecting A refers B, B refers A patterns"
              />
            </div>
          </div>
        )}

        {/* Payment Settings */}
        {activeTab === 'payment' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Payment Anomaly Detection
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Max Payment Attempts per Hour"
                value={settings.paymentAnomalies.maxPaymentAttemptsPerHour}
                onChange={(val) => updateSetting('paymentAnomalies', 'maxPaymentAttemptsPerHour', val)}
                type="number"
                min={5}
                max={50}
                help="Alert when too many payment attempts in short time"
              />
              
              <SettingInput
                label="Max Failed Payments Before Alert"
                value={settings.paymentAnomalies.maxFailedPayments}
                onChange={(val) => updateSetting('paymentAnomalies', 'maxFailedPayments', val)}
                type="number"
                min={2}
                max={20}
                help="Alert after this many consecutive failed payments"
              />
              
              <SettingInput
                label="Unusual Amount Threshold ($)"
                value={settings.paymentAnomalies.unusualAmountThreshold}
                onChange={(val) => updateSetting('paymentAnomalies', 'unusualAmountThreshold', val)}
                type="number"
                min={100}
                max={5000}
                help="Alert for payments significantly above average"
              />
              
              <SettingInput
                label="Multiple Payment Methods (24hr)"
                value={settings.paymentAnomalies.multiplePaymentMethodsThreshold}
                onChange={(val) => updateSetting('paymentAnomalies', 'multiplePaymentMethodsThreshold', val)}
                type="number"
                min={2}
                max={10}
                help="Alert when using many different cards in 24 hours"
              />
              
              <SettingToggle
                label="Geographic Inconsistency Detection"
                value={settings.paymentAnomalies.geographicInconsistency}
                onChange={(val) => updateSetting('paymentAnomalies', 'geographicInconsistency', val)}
                help="Alert when payment location doesn't match user location"
              />
            </div>
          </div>
        )}

        {/* Request Patterns */}
        {activeTab === 'request' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Song Request Patterns
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Max Requests per Hour"
                value={settings.requestPatterns.maxRequestsPerHour}
                onChange={(val) => updateSetting('requestPatterns', 'maxRequestsPerHour', val)}
                type="number"
                min={5}
                max={100}
                help="Alert when user makes too many song requests per hour"
              />
              
              <SettingInput
                label="Max Requests to Same Musician (Daily)"
                value={settings.requestPatterns.maxRequestsSameMusicianDay}
                onChange={(val) => updateSetting('requestPatterns', 'maxRequestsSameMusicianDay', val)}
                type="number"
                min={3}
                max={50}
                help="Alert for excessive requests to single musician"
              />
              
              <SettingInput
                label="Min Time Between Requests (seconds)"
                value={settings.requestPatterns.minTimeBetweenRequestsSeconds}
                onChange={(val) => updateSetting('requestPatterns', 'minTimeBetweenRequestsSeconds', val)}
                type="number"
                min={10}
                max={300}
                help="Alert when requests made too rapidly"
              />
              
              <SettingInput
                label="Request to Payment Ratio Alert (%)"
                value={settings.requestPatterns.requestToPaymentRatioThreshold}
                onChange={(val) => updateSetting('requestPatterns', 'requestToPaymentRatioThreshold', val)}
                type="number"
                min={5}
                max={50}
                help="Alert when payment rate is suspiciously low"
              />
            </div>
          </div>
        )}

        {/* Performance/Rating Manipulation */}
        {activeTab === 'performance' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Rating & Performance Monitoring
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Max Ratings per Day (per user)"
                value={settings.performanceManipulation.maxRatingsPerDay}
                onChange={(val) => updateSetting('performanceManipulation', 'maxRatingsPerDay', val)}
                type="number"
                min={5}
                max={50}
                help="Alert when user submits too many ratings in one day"
              />
              
              <SettingInput
                label="Rating Velocity per Hour"
                value={settings.performanceManipulation.ratingVelocityPerHour}
                onChange={(val) => updateSetting('performanceManipulation', 'ratingVelocityPerHour', val)}
                type="number"
                min={2}
                max={20}
                help="Alert for rapid-fire rating submissions"
              />
              
              <SettingInput
                label="Min Performance Duration (seconds)"
                value={settings.performanceManipulation.minPerformanceDurationSeconds}
                onChange={(val) => updateSetting('performanceManipulation', 'minPerformanceDurationSeconds', val)}
                type="number"
                min={10}
                max={120}
                help="Alert for suspiciously short performance uploads"
              />
              
              <SettingToggle
                label="Consistent Rating Pattern Detection"
                value={settings.performanceManipulation.consistentRatingDetection}
                onChange={(val) => updateSetting('performanceManipulation', 'consistentRatingDetection', val)}
                help="Alert when detecting repetitive rating patterns"
              />
              
              <SettingToggle
                label="Same IP Rating Detection"
                value={settings.performanceManipulation.sameIpRatingDetection}
                onChange={(val) => updateSetting('performanceManipulation', 'sameIpRatingDetection', val)}
                help="Alert when multiple ratings from same IP"
              />
              
              <SettingToggle
                label="Performance Recycling Detection"
                value={settings.performanceManipulation.performanceRecyclingDetection}
                onChange={(val) => updateSetting('performanceManipulation', 'performanceRecyclingDetection', val)}
                help="Alert when same performance used multiple times"
              />
            </div>
          </div>
        )}

        {/* Geographic Anomalies */}
        {activeTab === 'geographic' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Geographic & Session Monitoring
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Multiple Locations in 24hrs"
                value={settings.geographicAnomalies.multipleLocationThreshold}
                onChange={(val) => updateSetting('geographicAnomalies', 'multipleLocationThreshold', val)}
                type="number"
                min={2}
                max={10}
                help="Alert when activity from too many cities"
              />
              
              <SettingInput
                label="Max Concurrent Sessions"
                value={settings.geographicAnomalies.maxConcurrentSessions}
                onChange={(val) => updateSetting('geographicAnomalies', 'maxConcurrentSessions', val)}
                type="number"
                min={1}
                max={10}
                help="Alert for too many simultaneous logins"
              />
              
              <SettingInput
                label="Session Hopping per Hour"
                value={settings.geographicAnomalies.sessionHoppingThreshold}
                onChange={(val) => updateSetting('geographicAnomalies', 'sessionHoppingThreshold', val)}
                type="number"
                min={5}
                max={50}
                help="Alert for excessive login/logout activity"
              />
              
              <SettingToggle
                label="Impossible Travel Detection"
                value={settings.geographicAnomalies.impossibleTravelDetection}
                onChange={(val) => updateSetting('geographicAnomalies', 'impossibleTravelDetection', val)}
                help="Alert for physically impossible location changes"
              />
              
              <SettingToggle
                label="VPN/Proxy Detection"
                value={settings.geographicAnomalies.vpnProxyDetection}
                onChange={(val) => updateSetting('geographicAnomalies', 'vpnProxyDetection', val)}
                help="Alert when detecting VPN or proxy usage"
              />
            </div>
          </div>
        )}

        {/* Financial Thresholds */}
        {activeTab === 'financial' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Financial Thresholds
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Daily Earning Limit Alert ($)"
                value={settings.financialThresholds.dailyEarningLimit}
                onChange={(val) => updateSetting('financialThresholds', 'dailyEarningLimit', val)}
                type="number"
                min={100}
                max={10000}
                help="Alert when musician earns more than this in one day"
              />
              
              <SettingInput
                label="Weekly Earning Limit Alert ($)"
                value={settings.financialThresholds.weeklyEarningLimit}
                onChange={(val) => updateSetting('financialThresholds', 'weeklyEarningLimit', val)}
                type="number"
                min={500}
                max={50000}
                help="Alert when musician earns more than this in one week"
              />
              
              <SettingInput
                label="Voucher Redemptions per Week"
                value={settings.financialThresholds.voucherRedemptionVelocity}
                onChange={(val) => updateSetting('financialThresholds', 'voucherRedemptionVelocity', val)}
                type="number"
                min={1}
                max={20}
                help="Alert for excessive voucher redemptions"
              />
              
              <SettingInput
                label="Unusual Voucher Amount ($)"
                value={settings.financialThresholds.unusualVoucherAmount}
                onChange={(val) => updateSetting('financialThresholds', 'unusualVoucherAmount', val)}
                type="number"
                min={100}
                max={5000}
                help="Alert for unusually high voucher amounts"
              />
            </div>
          </div>
        )}

        {/* Risk Scoring */}
        {activeTab === 'risk' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Risk Score Configuration
            </h3>
            
            <div style={{ marginBottom: '24px' }}>
              <p style={{ color: '#9CA3AF', marginBottom: '16px' }}>
                Adjust the weight of each factor in calculating overall risk scores (must total 100)
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <RiskWeightSlider
                  label="Account Velocity"
                  value={settings.riskScoring.accountVelocityWeight}
                  onChange={(val) => updateSetting('riskScoring', 'accountVelocityWeight', val)}
                />
                
                <RiskWeightSlider
                  label="Referral Patterns"
                  value={settings.riskScoring.referralPatternWeight}
                  onChange={(val) => updateSetting('riskScoring', 'referralPatternWeight', val)}
                />
                
                <RiskWeightSlider
                  label="Payment Anomalies"
                  value={settings.riskScoring.paymentAnomalyWeight}
                  onChange={(val) => updateSetting('riskScoring', 'paymentAnomalyWeight', val)}
                />
                
                <RiskWeightSlider
                  label="Performance Patterns"
                  value={settings.riskScoring.performancePatternWeight}
                  onChange={(val) => updateSetting('riskScoring', 'performancePatternWeight', val)}
                />
                
                <RiskWeightSlider
                  label="Geographic Anomalies"
                  value={settings.riskScoring.geographicAnomalyWeight}
                  onChange={(val) => updateSetting('riskScoring', 'geographicAnomalyWeight', val)}
                />
              </div>
              
              <div style={{ marginTop: '20px' }}>
                <p style={{ color: '#9CA3AF', marginBottom: '8px' }}>
                  Total Weight: {
                    settings.riskScoring.accountVelocityWeight +
                    settings.riskScoring.referralPatternWeight +
                    settings.riskScoring.paymentAnomalyWeight +
                    settings.riskScoring.performancePatternWeight +
                    settings.riskScoring.geographicAnomalyWeight
                  }%
                </p>
              </div>
            </div>
            
            <div style={{ borderTop: '1px solid #374151', paddingTop: '20px' }}>
              <SettingInput
                label="Overall Risk Alert Threshold"
                value={settings.riskScoring.overallRiskThreshold}
                onChange={(val) => updateSetting('riskScoring', 'overallRiskThreshold', val)}
                type="number"
                min={50}
                max={100}
                help="Generate alert when risk score exceeds this value (0-100)"
              />
            </div>
          </div>
        )}

        {/* Alert Configuration */}
        {activeTab === 'alerts' && (
          <div>
            <h3 style={{ color: 'white', marginBottom: '24px', fontSize: '20px' }}>
              Alert Configuration
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <SettingInput
                label="Alert Threshold Score"
                value={settings.alertConfig.alertThreshold}
                onChange={(val) => updateSetting('alertConfig', 'alertThreshold', val)}
                type="number"
                min={50}
                max={100}
                help="Minimum risk score to generate an alert"
              />
              
              <SettingInput
                label="Alert Cooldown (hours)"
                value={settings.alertConfig.alertCooldownHours}
                onChange={(val) => updateSetting('alertConfig', 'alertCooldownHours', val)}
                type="number"
                min={1}
                max={168}
                help="Hours before generating another alert for same user"
              />
              
              <SettingInput
                label="Batch Alert Frequency (minutes)"
                value={settings.alertConfig.batchAlertFrequencyMinutes}
                onChange={(val) => updateSetting('alertConfig', 'batchAlertFrequencyMinutes', val)}
                type="number"
                min={15}
                max={1440}
                help="How often to send batch alert summaries"
              />
              
              <SettingToggle
                label="Email Notifications"
                value={settings.alertConfig.emailNotifications}
                onChange={(val) => updateSetting('alertConfig', 'emailNotifications', val)}
                help="Send email alerts to administrators"
              />
              
              <SettingToggle
                label="SMS for Critical Alerts"
                value={settings.alertConfig.smsNotificationsForCritical}
                onChange={(val) => updateSetting('alertConfig', 'smsNotificationsForCritical', val)}
                help="Send SMS for high-risk alerts (90+ score)"
              />
              
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px',
                gridColumn: 'span 2'
              }}>
                <p style={{ color: 'white', marginBottom: '12px', fontWeight: 'bold' }}>
                  Dashboard Alert Priority
                </p>
                <select
                  value={settings.alertConfig.dashboardPriority}
                  onChange={(e) => updateSetting('alertConfig', 'dashboardPriority', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: '#1F2937',
                    color: 'white',
                    border: '1px solid #4B5563'
                  }}
                >
                  <option value="low">Low - Show in background</option>
                  <option value="medium">Medium - Show with indicator</option>
                  <option value="high">High - Show with notification</option>
                  <option value="critical">Critical - Show immediately</option>
                </select>
              </div>
              
              <div style={{
                backgroundColor: '#FEF3C7',
                borderRadius: '8px',
                padding: '16px',
                gridColumn: 'span 2',
                display: 'flex',
                gap: '12px'
              }}>
                <AlertCircle size={20} style={{ color: '#D97706', flexShrink: 0 }} />
                <div>
                  <p style={{ color: '#92400E', margin: '0 0 4px 0', fontWeight: 'bold' }}>
                    Auto-Suspend Disabled
                  </p>
                  <p style={{ color: '#78350F', margin: 0, fontSize: '14px' }}>
                    Automatic account suspension is permanently disabled. 
                    All flagged accounts require manual admin review and action.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const SettingInput = ({ label, value, onChange, type = 'number', min, max, help }) => (
  <div style={{
    backgroundColor: '#374151',
    borderRadius: '8px',
    padding: '16px'
  }}>
    <label style={{ display: 'block' }}>
      <p style={{ color: 'white', margin: '0 0 8px 0', fontWeight: 'bold' }}>
        {label}
      </p>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(type === 'number' ? parseInt(e.target.value) : e.target.value)}
        min={min}
        max={max}
        style={{
          width: '100%',
          padding: '8px',
          borderRadius: '4px',
          backgroundColor: '#1F2937',
          color: 'white',
          border: '1px solid #4B5563'
        }}
      />
      {help && (
        <p style={{ color: '#9CA3AF', margin: '8px 0 0 0', fontSize: '12px' }}>
          {help}
        </p>
      )}
    </label>
  </div>
);

const SettingToggle = ({ label, value, onChange, help }) => (
  <div style={{
    backgroundColor: '#374151',
    borderRadius: '8px',
    padding: '16px'
  }}>
    <label style={{ 
      display: 'flex', 
      alignItems: 'start', 
      justifyContent: 'space-between',
      cursor: 'pointer'
    }}>
      <div style={{ flex: 1 }}>
        <p style={{ color: 'white', margin: '0 0 4px 0', fontWeight: 'bold' }}>
          {label}
        </p>
        {help && (
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: '12px' }}>
            {help}
          </p>
        )}
      </div>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ 
          width: '20px', 
          height: '20px', 
          cursor: 'pointer',
          accentColor: '#7C3AED',
          marginLeft: '12px'
        }}
      />
    </label>
  </div>
);

const RiskWeightSlider = ({ label, value, onChange }) => (
  <div style={{
    backgroundColor: '#374151',
    borderRadius: '8px',
    padding: '16px'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
      <p style={{ color: 'white', margin: 0, fontWeight: 'bold' }}>
        {label}
      </p>
      <p style={{ color: '#7C3AED', margin: 0, fontWeight: 'bold' }}>
        {value}%
      </p>
    </div>
    <input
      type="range"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      min={0}
      max={100}
      step={5}
      style={{
        width: '100%',
        accentColor: '#7C3AED'
      }}
    />
  </div>
);

export default AIFraudSettings;